import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { SNACKBAR_DISMISS, SnackbarRef } from './snackbar-ref';
import { SnackbarConfig, SnackbarData } from './snackbar.types';

export interface SnackbarState {
  config: SnackbarConfig;
  ref: SnackbarRef;
}

@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  private readonly snackbarSubject = new Subject<SnackbarState | null>();
  readonly snackbar$ = this.snackbarSubject.asObservable();

  private activeRef: SnackbarRef | null = null;
  private autoHideTimeout: ReturnType<typeof setTimeout> | null = null;

  open(
    message: string,
    action?: string,
    config?: { duration?: number; state?: 'ok' | 'error' | 'warn' | '' }
  ): SnackbarRef {
    return this.show({
      message,
      action,
      state: config?.state ?? '',
      duration: config?.duration ?? 4000,
    });
  }

  status(data: SnackbarData, duration = 4000): SnackbarRef {
    return this.show({
      message: data.message,
      action: data.action,
      icon: data.icon,
      state: data.state,
      duration,
    });
  }

  dismiss(): void {
    this.dismissActive();
  }

  private show(config: SnackbarConfig): SnackbarRef {
    this.dismissActive();

    const ref = new SnackbarRef();
    this.activeRef = ref;

    this.snackbarSubject.next({ config, ref });

    ref.afterDismissed().subscribe(() => {
      if (this.activeRef === ref) {
        this.activeRef = null;
        this.snackbarSubject.next(null);
      }
    });

    if (config.duration && config.duration > 0) {
      this.autoHideTimeout = setTimeout(() => {
        if (this.activeRef === ref) {
          this.dismissActive();
        }
      }, config.duration);
    }

    return ref;
  }

  private dismissActive(): void {
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = null;
    }

    if (this.activeRef) {
      this.activeRef[SNACKBAR_DISMISS]();
      this.activeRef = null;
      this.snackbarSubject.next(null);
    }
  }
}
